import React, { useEffect, useState, useCallback } from "react";
import { useForm } from "react-hook-form";
import {
	Flex,
	Heading,
	FormErrorMessage,
	FormLabel,
	FormControl,
	Input,
	Button,
	Box,
	Divider,
	Select,
	RadioGroup,
	Stack,
	Radio,
	Checkbox,
	Textarea,
	UnorderedList,
	ListItem,
	ListIcon,
	useToast,
	Image,
	Text,
	Center,
	Icon,
	useMediaQuery,
} from "@chakra-ui/react";
import { AttachmentIcon } from "@chakra-ui/icons";
import { useMutation, useQuery } from "react-query";
import Dropzone from "react-dropzone";
import Section from "../../Component/Layout/Section";
import { getPost, postApplication, postPost, uploadFile } from "../../service";
import { theme } from "../../assets/style/theme";
import { FieldItem } from "../../types";
import FormHeader from "../../Widget/FormHeader";
import { MdCheck } from "react-icons/md";

declare global {
  interface Window {
    __LANG__: 'en' | 'ko';
  }
}


type Props = {
	params: any;
};

function Form({ params }: Props) {
	const [fieldGroup, setFieldGroup] = useState([] as any);
	const [attachedFiles, setAttachedFiles] = useState({} as any);
	const [isFinished, setIsFinished] = useState(false);
	const toast = useToast();

	const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`);

	const { isLoading, error, data } = useQuery("getPage_form", () => getPost(params.form_id));

	const mutation = useMutation(
		(data: any) => {
			return postApplication(data);
		},
		{
			onSuccess: () => {
				// toast({
				//   title: '양식 제출',
				//   description: '신청이 성공적으로 완료되었습니다.',
				//   status: 'success',
				//   position: 'top',
				// });
				setIsFinished(true);
			},
			onError: (err: any) => {
				toast({
					title: "전송 실패",
					description: err.response.data.error,
					status: "error",
					position: "top",
				});
			},
		}
	);

	const {
		handleSubmit,
		register,
		formState: { errors, isSubmitting },
	} = useForm<any>();

	function onSubmit(values: any) {
		// TODO: 첨부 파일 함꼐 전송
		// TODO: form_id 등 정보 전송
		let newApplication = {
			post_title: `[${data.title}] ${values.name || values.teamname} / ${values.email || values.teamemail}`,
			post_content: JSON.stringify({ ...values, attachedFiles: attachedFiles }),
			post_status: "private",
			post_type: "application",
			meta_input: {
				application_form_id: params.form_id,
				application_form_title: data.title,
				application_email: values.email,
				application_name: values.name,
			},
		};

		mutation.mutate(newApplication);
	}

	const onDrop = async (filedkey: string, files: any) => {
		// 형식 확인
		// if (!isImageType(files[0]?.type)) {
		// 	Alert.error(Alert?.msg?.FILE_IMAGE_TYPE);
		// 	return;
		// }

		// // maxsize 확인
		// if (files[0].size >= MAX_FILE_SIZE) {
		// 	Alert.error(Alert?.msg?.FILE_MAX_SIZE);
		// 	return;
		// }

		const formData = new FormData();
		Array.from(files).forEach((item: any) => {
			formData.append("file", item);
		});

		try {
			const result = await uploadFile(formData);

			if (result.hasOwnProperty("error")) {
				toast({
					title: "업로드 실패",
					description: result.error,
					status: "error",
					position: "top",
				});
				return;
			}

			if (result) {
				let n = {
					...attachedFiles,
					[filedkey]: [...(attachedFiles[filedkey] ? attachedFiles[filedkey] : []), result],
				};
				setAttachedFiles(n);

				toast({
					title: "업로드 성공",
					description: "파일 첨부가 완료되었습니다.",
					status: "success",
					position: "top",
				});
			}
		} catch (error: any) {
			console.dir(error);
		}
	};

	// function setBrowserTitle(title: string) {
	//   const newTitle = `${title} | 숲과나눔`
	//   document.title = title;
	// }

	useEffect(() => {
		if (data === undefined) {
			return;
		}
		// setBrowserTitle(data.title);
		setFieldGroup(JSON.parse(data.meta.form_formsettings));
	}, [data]);

	const isEnglish = window.__LANG__ === 'en';

	return (
		<>
			{isFinished && (
				<Box mt="100" mb="100" textAlign="center">
					<Box borderColor="b.lg" borderStyle="solid" borderWidth="1px" borderRadius="10" maxW="400" mr="auto" ml="auto" pt="80px" pb="80px">
						<Icon as={MdCheck} w={16} h={16} color="b.dgr" />
						<Text fontSize="20">
							{
								isEnglish ? 'Your application has been successfully completed.' : '신청이 성공적으로 완료되었습니다'
							}
						</Text>
					</Box>
				</Box>
			)}
			{!isLoading && data && !isFinished && (
				<Box maxW="800" mt={10} mb={10} ml="auto" mr="auto" pl={isMobile ? 6 : 0} pr={isMobile ? 6 : 0}>
					<FormHeader title={data.title} content={data.content} featured_full={data.featured_full} displayButton={true} detailButtonLabel={data?.meta?.form_detail_label?.[0]} url={data?.meta?.form_external_link?.[0]} />

					<Divider color="b.lg" mt={10} mb={10} />

					<form name="newForm" onSubmit={handleSubmit(onSubmit)} noValidate>
						<Box>
							{fieldGroup.map((group: any, index: number) => (
								<Box key={index}>
									<Heading pb={8}>{group.title}</Heading>
									<Flex direction={group.layout === "horizontal" ? "row" : "column"}>
										{group.fields.map((field: FieldItem, index: number) => (
											<Box key={index} w={field?.style?.width} pb={8}>
												<FormControl isInvalid={!!errors[field.key]}>
													{field.showLabel === "true" && (
														<FormLabel htmlFor={field.key} pb={2}>
															{field.required === "true" && (
																<Text as="span" fontSize="sm" color="red.500" pr={1}>
																	*
																</Text>
															)}
															{field.label}
															{field.placeholder && field.type !== "text" && field.type !== "textarea" && <Text pl={2} fontSize="13" as="span" dangerouslySetInnerHTML={{ __html: field.placeholder }} />}
														</FormLabel>
													)}
													{field.type === "text" && <Input id={field.key} placeholder={field.placeholder} {...register(field.key, { required: field.required === "true" })} />}
													{field.type === "email" && (
														<Input
															id={field.key}
															type="email"
															placeholder={field.placeholder}
															{...register(field.key, {
																required: field.required === "true",
																pattern: {
																	value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
																	message: "invalid email address",
																},
															})}
														/>
													)}
													{field.type === "select" && (
														<Select id={field.key} placeholder={field.placeholder} {...register(field.key, { required: field.required === "true" })}>
															{field.options.split(",").map((item: any, index: number) => (
																<option key={index} value={item}>
																	{item}
																</option>
															))}
														</Select>
													)}
													{field.type === "radio" && (
														<RadioGroup id={field.key} colorScheme="green">
															<Stack direction="row" pt={2}>
																{field.options.split(",").map((item: any, index: number) => (
																	<Radio colorScheme="green" key={index} {...register(field.key, { required: field.required === "true" })} value={item}>
																		{item}
																	</Radio>
																))}
															</Stack>
														</RadioGroup>
													)}
													{field.type === "checkbox" && (
														<Stack direction="column" pt={2}>
															{field.options.split(",").map((item: any, index: number) => (
																<Checkbox colorScheme="green" key={index} {...register(field.key, { required: field.required === "true" })} value={item}>
																	<Text whiteSpace="nowrap">{item}</Text>
																</Checkbox>
															))}
														</Stack>
													)}
													{field.type === "textarea" && <Textarea id={field.key} placeholder={field.placeholder} {...register(field.key, { required: field.required === "true" })} />}
													{field.type === "file" && (
														<>
															<Dropzone onDrop={(files: any) => onDrop(field.key, files)} maxFiles={1}>
																{({ getRootProps, getInputProps }) => (
																	<Box textAlign="center" border="1px" borderColor="b.lg" p="4">
																		<div {...getRootProps()}>
																			<input {...getInputProps()} />
																			{
																				isEnglish ? (
																					<p>Attach file: click or drag.  </p>
																				) : (
																					<p>클릭 또는 드래그로 파일을 첨부해 주세요.</p>
																				)
																			}
																		</div>
																	</Box>
																)}
															</Dropzone>
															{attachedFiles[field.key] && (
																<UnorderedList pt="2" pb="2" ml="0">
																	{attachedFiles[field.key].map((item: any, index: number) => (
																		<ListItem key={index} fontSize="xs">
																			<ListIcon as={AttachmentIcon} color="green.500" />
																			{item.url}
																		</ListItem>
																	))}
																</UnorderedList>
															)}
														</>
													)}
													{errors[field.key]?.type === "required" && <FormErrorMessage>필수 입력입니다.</FormErrorMessage>}
													{errors[field.key]?.type === "pattern" && <FormErrorMessage>형식에 맞지 않습니다.</FormErrorMessage>}
												</FormControl>
											</Box>
										))}
									</Flex>
									<Divider mt="4" mb="4" color="b.lg" />
								</Box>
							))}
							<Center>
								<Button mt={4} size="lg" colorScheme="dgr" isLoading={isSubmitting} type="submit">
									{data.meta?.form_button_label[0] || "신청하기"}
								</Button>
							</Center>
						</Box>
					</form>
				</Box>
			)}
		</>
	);
}

Form.defaultProps = {};

export default Form;
